import { useEffect, useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { useForm } from 'react-hook-form';
import ddt from '@wix/da-ddt';
import {
  DeviationSubmitActionPayloadType,
  SubmitFormValues,
} from '../redux/types';
import { submitDeviation } from '../redux/actions';
import { useSubmitFormAdapters } from './useSubmitFormAdapters';
import { useSubmitInitialization } from './useSubmitInitialization';
import { useSubmitValidationResolver } from './useSubmitValidationResolver';
import { useSafeClose } from './useSafeClose';
import { useScrollToField } from './useScrollToField';
import { useSubmitPubSub } from './useSubmitPubSub';
import { SubmitFormParams } from '../types';

const logger = ddt.createLogger('submit');

interface UseSubmitFormParams {
  params: SubmitFormParams;
  requestClose: VoidFunction;
}

export function useSubmitForm({ params, requestClose }: UseSubmitFormParams) {
  const dispatch = useDispatch();
  const { formStatus, initResponse, refreshInitData } = useSubmitInitialization(
    params.deviationId
  );

  const isDraft = !!params.isDraft;
  const isEdit = useMemo(
    () => !isDraft && (initResponse?.published ?? params.isEdit ?? false),
    [initResponse, isDraft, params]
  );

  const {
    registerValidator,
    registerSubmitAdapter,
    unregisterValidator,
    unregisterSubmitAdapter,
    submitAdapter,
    validationSchema,
    registerNestedForm,
    unregisterNestedForm,
    hasNestedForms,
    submitAllNestedForms,
  } = useSubmitFormAdapters(initResponse, params);

  const resolver = useSubmitValidationResolver(validationSchema);

  // Initialize the react-hook-form context
  const formContext = useForm<SubmitFormValues>({
    mode: 'onChange',
    reValidateMode: 'onChange',
    resolver,
    // note: default values are set on a field level, so we *cannot* put them here
  });

  const { handleSubmit, register, reset, watch, setValue } = formContext;

  // Register the virtual form fields
  useEffect(() => {
    // These are virtual fields that we keep in the form state for easier access
    register('isPurchasable');
    register('isInitialized');
  }, [register]);

  // Create callbacks
  const onError = useCallback(
    data => logger.error(`Error: ${JSON.stringify(data)}`),
    []
  );

  const requestSubmit = useCallback(
    async (
      options: Pick<
        DeviationSubmitActionPayloadType,
        'doPublish' | 'doRedirect' | 'onSuccess'
      >
    ) => {
      // allows validators to verify if saving as draft or publishing, eg. FilePreview/validation.ts
      register('isPublishing');
      setValue('isPublishing', options.doPublish);

      // Handle nested forms first if they exist
      if (hasNestedForms()) {
        try {
          logger.log('Submitting nested forms...');
          const nestedResults = await submitAllNestedForms();
          logger.log('All nested forms submitted successfully:', nestedResults);

          // Store nested results in the main form for reference
          nestedResults.forEach(({ name, data }) => {
            register(name);
            setValue(name, data);
          });
        } catch (error) {
          logger.error('Nested form submission failed:', error);
          // Call onError to handle nested form validation failures
          onError({ nestedFormError: error.message });
          return;
        }
      }

      // Submit the main form
      handleSubmit(
        data =>
          dispatch(
            submitDeviation({
              contextId: 'submit-form',
              formData: submitAdapter(data, formContext.formState),
              ...options,
            })
          ),
        onError
      )();
    },
    [
      formContext,
      handleSubmit,
      submitAdapter,
      onError,
      dispatch,
      setValue,
      register,
      hasNestedForms,
      submitAllNestedForms,
    ]
  );

  const { scrollableRef, scrollToField } = useScrollToField();
  const { requestDeinitializeAndClose, requestSafeClose } = useSafeClose(
    isEdit,
    isDraft,
    formContext,
    formStatus,
    requestClose
  );

  const isInitialized = watch('isInitialized');
  useEffect(() => {
    if (initResponse && !isInitialized) {
      logger.log('Loaded initialization data');
      reset({
        isInitialized: true,
        isPurchasable: !!initResponse.pcp,
      });
    }
  }, [initResponse, isInitialized, reset]);

  const pubsub = useSubmitPubSub();

  return {
    formContext,
    params,
    initResponse,
    isEdit,
    isDraft,
    registerValidator,
    registerSubmitAdapter,
    unregisterValidator,
    unregisterSubmitAdapter,
    registerNestedForm,
    unregisterNestedForm,
    hasNestedForms,
    submitAllNestedForms,
    refreshInitData,
    requestSubmit,
    requestDeinitializeAndClose,
    requestSafeClose,
    scrollableRef,
    scrollToField,
    pubsub,
    submitAdapter,
  };
}
